import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import apontamentoService from '../../services/apontamento.service';
import DuracaoModal, { AjusteMultiploModal, PASSO_MINUTOS, opcoesDeHora,
  ehMultiploDoPasso, somarMinutos } from '../../components/DuracaoModal';

// ════════════════════════════════════════════════════════════════════════════
// ApontamentoModal — sub-modal pra criar ou editar um apontamento
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   - aberto: boolean
//   - tarefaId: id da tarefa em que vai apontar
//   - tarefaTitulo: pra exibir no header
//   - apontamento: null pra novo, ou objeto pra editar
//   - onFechar: callback
//   - onSalvar: callback após salvar com sucesso
//
// r185:
//   - tela mais larga, com a descrição ganhando espaço de verdade;
//   - data e hora em campos separados (início e fim);
//   - cabeçalho no azul padrão do portal;
//   - com o início preenchido, abre a tela de Duração (horas + 00/15/30/45)
//     que calcula o fim automaticamente — o fim continua editável na mão.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#10b981', verdeBg: '#f0fff4',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

const textareaStyle = {
  ...inputStyle, height: 'auto', minHeight: '150px', padding: '8px 10px',
  resize: 'vertical', lineHeight: '1.45',
};

// ─── Helpers de formato ────────────────────────────────────────────────────
// r185: o form guarda data e hora SEPARADAS (dataIni/horaIni, dataFim/horaFim).
// O payload continua sendo 'YYYY-MM-DDTHH:mm', igual ao datetime-local de antes.
function partesDeIso(iso) {
  if (!iso) return { data: '', hora: '' };
  const d = new Date(iso);
  if (isNaN(d)) return { data: '', hora: '' };
  const off = d.getTimezoneOffset() * 60000;
  const local = new Date(d.getTime() - off).toISOString();
  return { data: local.slice(0, 10), hora: local.slice(11, 16) };
}

function juntar(data, hora) {
  if (!data || !hora) return '';
  return `${data}T${hora}`;
}

function calcularHorasDecimal(iniStr, fimStr) {
  if (!iniStr || !fimStr) return 0;
  const ms = new Date(fimStr).getTime() - new Date(iniStr).getTime();
  if (!ms || ms <= 0) return 0;
  return Math.round((ms / 3600000) * 100) / 100;
}

function formatarHoras(h) {
  if (!h && h !== 0) return '—';
  const num = Number(h);
  const horas = Math.floor(num);
  const min   = Math.round((num - horas) * 60);
  if (horas === 0)             return `${min}min`;
  if (min === 0)               return `${horas}h`;
  return `${horas}h${String(min).padStart(2,'0')}`;
}

// r207: somarMinutos agora vem de components/DuracaoModal (uma cópia só).

function Campo({ label, obrigatorio, children }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{
        display: 'block', fontSize: '12px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ─── Par data + hora ───────────────────────────────────────────────────────
function CampoDataHora({ label, obrigatorio, data, hora, onData, onHora, onSairDoGrupo,
                         aoDigitarForaDaGrade, desabilitado, aviso }) {
  // onBlur no container: em React o handler e o focusout, que borbulha. Assim
  // trocar de data pra hora (dentro do proprio par) NAO conta como "sair".
  const aoPerderFoco = (e) => {
    if (!onSairDoGrupo) return;
    if (e.currentTarget.contains(e.relatedTarget)) return;
    onSairDoGrupo();
  };
  // r192: bloqueado enquanto o pré-requisito não estiver preenchido.
  const estiloBase = desabilitado
    ? { ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted, cursor: 'not-allowed' }
    : inputStyle;
  return (
    <Campo label={label} obrigatorio={obrigatorio}>
      <div style={{ display: 'flex', gap: '8px' }} onBlur={aoPerderFoco}>
        <input type="date" value={data} onChange={e => onData(e.target.value)}
               disabled={desabilitado}
               style={{ ...estiloBase, flex: '1 1 60%' }} />
        {/* r207: step de 15min na setinha/relógio do browser.
            r208: o que for digitado fora da grade NÃO é arredondado sozinho —
            o modal pergunta qual das duas opções válidas o usuário quer. */}
        <input type="time" value={hora} onChange={e => onHora(e.target.value)}
               onBlur={e => {
                 const opcoes = opcoesDeHora(e.target.value);
                 if (opcoes.length && aoDigitarForaDaGrade) {
                   aoDigitarForaDaGrade(e.target.value, opcoes);
                 }
               }}
               step={PASSO_MINUTOS * 60} disabled={desabilitado}
               style={{ ...estiloBase, flex: '1 1 40%' }} />
      </div>
      {desabilitado && aviso && (
        <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>{aviso}</div>
      )}
    </Campo>
  );
}

// r207: a tela de Duração virou componente compartilhado em
// components/DuracaoModal.jsx — o lançamento de horas usa a mesma.

// ════════════════════════════════════════════════════════════════════════════
function ApontamentoModal({ aberto, tarefaId, tarefaTitulo, apontamento, dadosIniciais, apontarComForce, onFechar, onSalvar }) {
  const { usuario } = useAuth();
  const ehNovo = !apontamento;

  const [form, setForm] = useState({
    dataIni: '', horaIni: '', dataFim: '', horaFim: '', descricao: '', faturavel: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [duracaoAberta, setDuracaoAberta] = useState(false);
  // r208: { campo, valor, opcoes } enquanto o usuário escolhe entre as duas
  // possibilidades válidas de um horário digitado fora da grade de 15min.
  const [ajusteHora, setAjusteHora] = useState(null);

  // r186: guarda o início pra qual a duração já foi oferecida. Serve pra tela
  // não reaparecer toda vez que o foco passa pelo campo sem nada mudar.
  const inicioOferecido = useRef(null);

  // ── Inicializa form ────────────────────────────────────────────────────
  useEffect(() => {
    if (!aberto) return;
    setErro('');
    setDuracaoAberta(false);
    inicioOferecido.current = null;

    if (ehNovo) {
      // Prioridade: dadosIniciais (do overlay de seleção visual)
      if (dadosIniciais?.data_inicio && dadosIniciais?.data_fim) {
        const pi = partesDeIso(dadosIniciais.data_inicio);
        const pf = partesDeIso(dadosIniciais.data_fim);
        // Veio do calendário com início E fim escolhidos: só oferece a duração
        // se o usuário mudar o início.
        inicioOferecido.current = juntar(pi.data, pi.hora);
        setForm({
          dataIni: pi.data, horaIni: pi.hora,
          dataFim: pf.data, horaFim: pf.hora,
          descricao: dadosIniciais.descricao || '',
          faturavel: '',
        });
      } else {
        // r192: só a data vem preenchida (hoje). A hora de início entra em
        // branco — é ela que destrava o fim e a tela de duração.
        const pi = partesDeIso(new Date().toISOString());
        setForm({
          dataIni: pi.data, horaIni: '',
          dataFim: '', horaFim: '',
          descricao: '', faturavel: '',
        });
      }
    } else {
      const pi = partesDeIso(apontamento.data_inicio);
      const pf = partesDeIso(apontamento.data_fim);
      // Edição: idem — só oferece se o início for alterado.
      inicioOferecido.current = juntar(pi.data, pi.hora);
      setForm({
        dataIni: pi.data, horaIni: pi.hora,
        dataFim: pf.data, horaFim: pf.hora,
        descricao: apontamento.descricao || '',
        faturavel: (apontamento.faturavel === 1 || apontamento.faturavel === '1') ? 'S' : 'N',
      });
    }
  }, [aberto, apontamento, dadosIniciais, ehNovo]);

  const dataInicioStr = juntar(form.dataIni, form.horaIni);
  const dataFimStr    = juntar(form.dataFim, form.horaFim);

  // r186: a tela de duração aparece ao SAIR dos campos de início com data E
  // hora preenchidas — não na abertura do modal (o início já vem preenchido,
  // e antes isso fazia a tela pular na cara ao abrir o apontamento).
  const aoSairDoInicio = () => {
    if (!dataInicioStr) return;                       // falta data ou hora
    if (inicioOferecido.current === dataInicioStr) return;   // nada mudou
    inicioOferecido.current = dataInicioStr;
    setDuracaoAberta(true);
  };

  // ESC fecha (a duração primeiro, se estiver aberta)
  useEffect(() => {
    if (!aberto) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (duracaoAberta) setDuracaoAberta(false);
      else onFechar();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [aberto, onFechar, duracaoAberta]);

  const setCampo = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  // r208: abre a escolha das duas opções válidas pro horário digitado.
  const pedirAjuste = (campo) => (valor, opcoes) => setAjusteHora({ campo, valor, opcoes });
  const handleCampo = (e) => setCampo(e.target.name, e.target.value);

  // Aplica a duração escolhida: preenche o fim, que segue editável na mão.
  const aplicarDuracao = (minutos) => {
    if (dataInicioStr && minutos > 0) {
      const fim = somarMinutos(dataInicioStr, minutos);
      setForm(f => ({ ...f, dataFim: fim.data, horaFim: fim.hora }));
    }
    setDuracaoAberta(false);
  };

  const horasCalc = calcularHorasDecimal(dataInicioStr, dataFimStr);
  // r175: faturável só vale para início <= agora (futuro = planejado)
  const inicioFuturo = !!dataInicioStr && new Date(dataInicioStr) > new Date();


  // ── Salvar com tratamento de conflito 409 ──────────────────────────────
  const enviar = useCallback(async (force) => {
    setErro('');
    if (!dataInicioStr || !dataFimStr) {
      setErro('Informe data e hora de início e de fim.');
      return;
    }
    if (horasCalc <= 0) {
      setErro('Duração deve ser maior que zero.');
      return;
    }
    // r207: início e fim só em múltiplos de 15 minutos (00, 15, 30, 45).
    // r208: se o usuário recusou o ajuste na hora, é aqui que a gente segura.
    const foraDaGrade = [];
    if (!ehMultiploDoPasso(form.horaIni)) foraDaGrade.push(`início (${form.horaIni})`);
    if (!ehMultiploDoPasso(form.horaFim)) foraDaGrade.push(`fim (${form.horaFim})`);
    if (foraDaGrade.length) {
      setErro(`Horário de ${foraDaGrade.join(' e de ')} fora dos ${PASSO_MINUTOS} minutos — use 00, 15, 30 ou 45.`);
      return;
    }
    if (!form.faturavel) {
      setErro('Informe se as horas são faturáveis.');
      return;
    }
    if (form.faturavel === 'S' && inicioFuturo) {
      setErro('Horas faturáveis só para início menor ou igual a agora (datas futuras = planejamento).');
      return;
    }

    try {
      setSalvando(true);
      const dados = {
        data_inicio: dataInicioStr,
        data_fim:    dataFimStr,
        descricao:   form.descricao.trim() || null,
        faturavel:   form.faturavel,   // 'S' | 'N'
      };

      if (ehNovo) {
        await apontamentoService.incluir(tarefaId, dados, usuario?.id, force);
      } else {
        await apontamentoService.alterar(apontamento.id, dados, usuario?.id, force);
      }
      onSalvar();
    } catch (err) {
      const resp = err.response;
      // 409 = conflito de horário no calendário
      if (resp?.status === 409 && resp.data?.requer_confirmacao) {
        const conflitos = resp.data.conflitos || [];
        const listaTxt = conflitos.map(c =>
          `• ${new Date(c.data_inicio).toLocaleString('pt-BR')} – ${new Date(c.data_fim).toLocaleString('pt-BR')}` +
          (c.tipo ? ` [${c.tipo}]` : '') +
          (c.observacao ? `: ${c.observacao}` : '')
        ).join('\n');

        const ok = window.confirm(
          `Já existe(m) ${conflitos.length} bloco(s) no calendário neste horário:\n\n` +
          listaTxt +
          `\n\nApontar mesmo assim (será marcado como sobreposto)?`
        );
        if (ok) {
          await enviar(true);  // reenvia com force=true
        }
        return;
      }
      setErro(resp?.data?.mensagem || 'Erro ao salvar apontamento.');
    } finally {
      setSalvando(false);
    }
  }, [form, dataInicioStr, dataFimStr, horasCalc, inicioFuturo, ehNovo, tarefaId, apontamento, usuario, onSalvar]);

  if (!aberto) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Manrope', sans-serif",
    }}>
      <div style={{
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        width: '760px', maxWidth: '95vw',
        maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header — r185 item 3: azul padrão do portal */}
        <div style={{
          padding: '12px 18px', backgroundColor: COR.azul,
          display: 'flex', alignItems: 'flex-start', gap: '12px',
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '14px', fontWeight: '700', color: COR.branco,
              fontFamily: 'Calibri, "Segoe UI", Arial, sans-serif',
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              {ehNovo ? '⏱ Apontar horas' : '✎ Editar apontamento'}
            </div>
            {tarefaTitulo && (
              <div style={{ fontSize: '11px', color: '#c9d6f5', marginTop: '2px' }}>
                em #{tarefaId} {tarefaTitulo}
              </div>
            )}
          </div>
          <button type="button" onClick={onFechar} title="Fechar" style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '18px', lineHeight: '1', cursor: 'pointer',
            padding: '0 2px', flexShrink: 0,
          }}>×</button>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px 18px', overflowY: 'auto' }}>
          {/* r185 item 2: data e hora separadas, início e fim */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px' }}>
              <CampoDataHora
                label="Início" obrigatorio
                data={form.dataIni} hora={form.horaIni}
                onData={v => setCampo('dataIni', v)}
                onHora={v => setCampo('horaIni', v)}
                onSairDoGrupo={aoSairDoInicio}
                aoDigitarForaDaGrade={pedirAjuste('horaIni')}
              />
            </div>
            <div style={{ flex: '1 1 300px' }}>
              <CampoDataHora
                label="Fim" obrigatorio
                data={form.dataFim} hora={form.horaFim}
                onData={v => setCampo('dataFim', v)}
                onHora={v => setCampo('horaFim', v)}
                aoDigitarForaDaGrade={pedirAjuste('horaFim')}
                desabilitado={!dataInicioStr}
                aviso="Preencha data e hora de início primeiro."
              />
            </div>
          </div>

          {/* Duração calculada + atalho pra recalcular o fim */}
          <div style={{
            padding: '8px 12px', marginBottom: '14px',
            backgroundColor: horasCalc > 0 ? COR.azulBg : COR.cinzaBg,
            border: `1px solid ${horasCalc > 0 ? COR.azul + '33' : COR.borda}`,
            borderRadius: '5px',
            fontSize: '12px', color: COR.texto,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '10px', flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: '600', color: COR.muted }}>Duração calculada:</span>
            <span style={{
              fontWeight: '700', marginRight: 'auto',
              color: horasCalc > 0 ? COR.azul : COR.muted,
            }}>
              {horasCalc > 0 ? `${formatarHoras(horasCalc)} (${horasCalc.toFixed(2)}h)` : '—'}
            </span>
            <button type="button"
                    onClick={() => setDuracaoAberta(true)}
                    disabled={!dataInicioStr}
                    title={dataInicioStr ? 'Calcular o fim a partir de uma duração' : 'Preencha data e hora de início'}
                    style={{
                      padding: '5px 12px', fontSize: '11px', fontWeight: '600',
                      border: `1px solid ${COR.azul}`, borderRadius: '5px',
                      backgroundColor: COR.branco, color: COR.azul,
                      cursor: dataInicioStr ? 'pointer' : 'not-allowed',
                      opacity: dataInicioStr ? 1 : 0.5,
                      fontFamily: "'Manrope', sans-serif",
                    }}>⏳ Definir duração</button>
          </div>

          {/* r185 item 1: descrição com espaço de verdade */}
          <Campo label="Descrição">
            <textarea name="descricao" value={form.descricao}
                      onChange={handleCampo}
                      placeholder="O que foi feito? (opcional)"
                      style={textareaStyle} />
          </Campo>

          {/* r174: Horas faturáveis (Sim exige projeto vinculado à tarefa) */}
          <div style={{ maxWidth: '320px' }}>
            <Campo label="Horas Faturáveis" obrigatorio>
              <select name="faturavel" value={form.faturavel} onChange={handleCampo}
                      style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">Selecione...</option>
                <option value="S" disabled={inicioFuturo}>Sim</option>
                <option value="N">Não</option>
              </select>
              {inicioFuturo && (
                <div style={{ fontSize: '11px', color: COR.muted, marginTop: '4px' }}>
                  "Sim" só para início ≤ agora — datas futuras são planejamento.
                </div>
              )}
            </Campo>
          </div>

          {erro && (
            <div style={{
              padding: '8px 12px', marginTop: '8px',
              backgroundColor: COR.vermBg,
              border: `1px solid ${COR.vermelho}`,
              borderRadius: '5px', color: COR.vermelho,
              fontSize: '12px', fontWeight: '600',
            }}>{erro}</div>
          )}
        </div>

        {/* Rodapé */}
        <div style={{
          padding: '12px 18px',
          borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
        }}>
          <button onClick={onFechar} style={{
            padding: '8px 18px', fontSize: '12px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
          }}>Cancelar</button>
          <button onClick={() => enviar(!!apontarComForce)} disabled={salvando || horasCalc <= 0} style={{
            padding: '8px 22px', fontSize: '12px', fontWeight: '700',
            border: `1px solid ${COR.azul}`, borderRadius: '5px',
            backgroundColor: COR.azul, color: COR.branco,
            cursor: (salvando || horasCalc <= 0) ? 'not-allowed' : 'pointer',
            opacity: (salvando || horasCalc <= 0) ? 0.6 : 1,
            fontFamily: "'Manrope', sans-serif",
          }}>{salvando ? 'Salvando...' : '✓ Salvar'}</button>
        </div>
      </div>

      {/* r208: escolha entre as duas opções válidas do horário */}
      {ajusteHora && (
        <AjusteMultiploModal
          valorDigitado={ajusteHora.valor}
          opcoes={ajusteHora.opcoes}
          onEscolher={(v) => { setCampo(ajusteHora.campo, v); setAjusteHora(null); }}
          onCancelar={() => setAjusteHora(null)}
        />
      )}

      {/* r185 item 4: tela de duração */}
      {duracaoAberta && (
        <DuracaoModal
          inicioStr={dataInicioStr}
          onAplicar={aplicarDuracao}
          onFechar={() => setDuracaoAberta(false)}
        />
      )}
    </div>
  );
}

export default ApontamentoModal;
